import { computed } from 'vue';
import { useFlag } from '@loadsmart/vue-feature-flags';
import useRecaptcha from './useRecaptcha';

export type RequestConfig = { url: string; headers?: Record<string, string> };

export default function useCometBffApi() {
  const cometBffEnabled = useFlag('enable-comet-bff');
  const isCometBffEnabled = computed(() => Boolean(cometBffEnabled.value?.enabled));
  const { getToken } = useRecaptcha();

  async function resolveWarehouse(warehouseId: string): Promise<RequestConfig> {
    if (isCometBffEnabled.value) {
      const token = await getToken('comet_initial');
      return { url: `/bff/comet/${warehouseId}/initial`, headers: { 'X-Recaptcha-Token': token } };
    }
    return { url: `/checkin/public/${warehouseId}/` };
  }

  async function resolveCompanyAutocomplete(warehouseId: string): Promise<RequestConfig> {
    if (isCometBffEnabled.value) {
      const token = await getToken('comet_company_autocomplete');
      return { url: '/bff/comet/company-autocomplete', headers: { 'X-Recaptcha-Token': token } };
    }
    return { url: `/checkin/public/${warehouseId}/company-autocomplete` };
  }

  async function resolveAppointment(
    warehouseId: string,
    identifier: string,
    geo: { latitude?: number; longitude?: number }
  ): Promise<RequestConfig> {
    const encoded = encodeURIComponent(identifier);
    if (isCometBffEnabled.value) {
      const token = await getToken('comet_appointment_lookup');
      return {
        url: `/bff/comet/${warehouseId}/appointment/${encoded}`,
        headers: { 'X-Recaptcha-Token': token }
      };
    }
    const lat = Number.isFinite(geo.latitude) ? geo.latitude : '';
    const lng = Number.isFinite(geo.longitude) ? geo.longitude : '';
    return {
      url: `/checkin/public/${encoded}/${warehouseId}/?latitude=${lat}&longitude=${lng}`
    };
  }

  async function resolveCheckinPlanned(warehouseId: string): Promise<RequestConfig> {
    if (isCometBffEnabled.value) {
      const token = await getToken('comet_checkin_planned');
      return { url: `/bff/comet/${warehouseId}`, headers: { 'X-Recaptcha-Token': token } };
    }
    return { url: `/checkin/public/${warehouseId}` };
  }

  async function resolveCheckinUnplanned(warehouseId: string): Promise<RequestConfig> {
    if (isCometBffEnabled.value) {
      const token = await getToken('comet_checkin_unplanned');
      return {
        url: `/bff/comet/${warehouseId}/unplanned`,
        headers: { 'X-Recaptcha-Token': token }
      };
    }
    return { url: `/checkin/public/${warehouseId}/unplanned` };
  }

  async function resolveCheckinRejected(warehouseId: string): Promise<RequestConfig> {
    if (isCometBffEnabled.value) {
      const token = await getToken('comet_checkin_rejected');
      return { url: `/bff/comet/${warehouseId}/reject`, headers: { 'X-Recaptcha-Token': token } };
    }
    return { url: `/checkin/public/${warehouseId}/reject` };
  }

  return {
    isCometBffEnabled,
    resolveWarehouse,
    resolveCompanyAutocomplete,
    resolveAppointment,
    resolveCheckinPlanned,
    resolveCheckinUnplanned,
    resolveCheckinRejected
  };
}
